import { Typography } from "@mui/material";
import {
  ChangeEvent,
  memo,
  useEffect,
  useMemo,
  useState,
  useCallback,
  MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Box,
  FlexColBox,
  Table,
  TableColumn,
  DarkTooltip,
  Link,
} from "../../common/components";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import { useMoment } from "../../common/hooks/useMoment";
import { Message, MessageTypes } from "../../components/Message/Message";
import { spacing, palette, createStyles } from "../../styles/theme";
import { getReducedCartItems, isQuoteRequest } from "../../utils/CartUtils";
import { OrderDetail } from "../../types";
import { generatePath } from "react-router-dom";
import { ROUTES } from "../../constant/common.constant";
import { ReactComponent as OrderRequestSearchIcon } from "../../assets/icons/order_request_search_icon.svg";
import { UserOrderExpandSection } from "./UserOrderExpandSection";
import { SortBy } from "../../common/components/Table/Table";
import OrderService from "../../service/OrderService";
import { useAuth } from "../../common/hooks/useAuth";

export interface OrderRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orderDate: string;
  clientProjectName: string;
  numberOfItems: number;
  orderDetail: OrderDetail;
  poNumber: string;
  shouldStartExpanded?: boolean;
  projectNumber: string;
}

const styles = createStyles({
  centerContainer: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    flex: "1",
    padding: {
      xs: `16px 16px ${spacing.xlMobile}`,
      lg: `${spacing.regularDesktop} ${spacing.largeDesktop} ${spacing.xlDesktop}`,
    },
    overflow: "auto",
    gap: spacing.groupingDesktop,
  },
});

const UserOrderList = () => {
  const { t } = useTranslation();
  const { isDistributor, isAdmin } = useAuth();
  const params = useParams();
  const { formatShortDate } = useMoment();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortBy | undefined>(undefined);
  const [ordersData, setOrdersData] = useState<{
    count: number;
    orders: OrderDetail[];
  }>({ count: 0, orders: [] });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  usePageTitle("pages.orders.title");

  //In case the page comes from checkout using preorder
  const orderId = params.orderId;

  const totalNumberOfPage = useMemo(() => {
    return Math.ceil(ordersData.count / rowsPerPage) || 0;
  }, [ordersData.count, rowsPerPage]);

  const setCurrentPage = useCallback(
    (_: MouseEvent<HTMLButtonElement> | null, pageNumber: number) => {
      if (pageNumber <= 0) {
        setPage(0);
      } else if (pageNumber >= totalNumberOfPage) {
        setPage(totalNumberOfPage);
      } else {
        setPage(pageNumber);
      }
    },
    [totalNumberOfPage]
  );

  //At load, fetch all preorders to get the latest information
  useEffect(() => {
    if (!isInitialLoading) {
      setIsRefetching(true);
    }
    OrderService.getMyOrders(page, rowsPerPage, searchTerm, sortBy).then(
      (res) => {
        const orders = res.data.data.preOrders;
        const count = res.data.data.count;
        setOrdersData({ orders, count });
        setIsInitialLoading(false);
        setIsRefetching(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, sortBy]);

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = isNaN(Number(event.target.value))
      ? 50
      : Number(event.target.value);
    setRowsPerPage(newValue);
    setPage(0);
  };

  const handleSearch = (searchTerm: string) => {
    setPage(0);
    setSearchTerm(searchTerm);
  };

  const columns: TableColumn<OrderRow>[] = [
    {
      width: "5%",
      labelKey: "",
      id: "collapsible",
      openByDefault: (row) => row.shouldStartExpanded ?? false,
      render: (row) => <UserOrderExpandSection orderDetail={row.orderDetail} />,
    },
    {
      width: isDistributor || isAdmin ? "25%" : "45%",
      labelKey: "all_orders.project",
      id: "projectNumber",
      sortable: true,
      render: (row) => {
        return (
          <Box
            display={"flex"}
            alignItems={"center"}
            gap={"2px"}
            flexDirection={"column"}
          >
            <Typography variant="textRegular">{row.projectNumber}</Typography>
            <Typography variant="textXs">{row.orderDate}</Typography>
          </Box>
        );
      },
    },
    {
      width: "30%",
      labelKey: "all_orders.client_project_name",
      id: "clientProjectName",
      sortable: true,
      hide: !isDistributor && !isAdmin,
      render: (row) => {
        return (
          <Box
            display={"flex"}
            alignItems={"center"}
            gap={"2px"}
            flexDirection={"column"}
          >
            <Typography variant="textRegular">
              {row.clientProjectName}
            </Typography>
          </Box>
        );
      },
    },
    {
      width: isDistributor || isAdmin ? "25%" : "45%",
      labelKey: "order_list.table.po_number",
      id: "poNumber",
      sortable: true,
      render: (row) => (
        <Typography
          variant="textRegular"
          color={row.poNumber ? palette.grey.nearBlack : palette.grey.primary}
        >
          {row.poNumber ? row.poNumber : "–"}
        </Typography>
      ),
    },
    {
      width: "5%",
      labelKey: "general.actions",
      id: "actions",
      align: "right",
      render: (row) => (
        <Box display="flex" gap={1} justifyContent="flex-end" pr="6px">
          <Link
            to={generatePath(ROUTES.ORDER, {
              orderId: row.orderDetail.preorder?.id,
            })}
            style={{
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            <DarkTooltip
              title={t("order_list.table.action.go_to_order")}
              placement="bottom"
            >
              <OrderRequestSearchIcon />
            </DarkTooltip>
          </Link>
        </Box>
      ),
    },
  ];

  const rows = useMemo((): OrderRow[] => {
    return (
      ordersData?.orders?.map((orderDetail) => {
        const techpackProjectNumber =
          orderDetail.techpack?.projectNumber ||
          (isQuoteRequest(orderDetail.pricing, orderDetail.techpack)
            ? t("all_orders.quote_request")
            : "–");

        const clientProjectName =
          orderDetail.clientDetails?.clientProjectName || "";

        const orderDate = formatShortDate(orderDetail.preorder?.createdAt);

        const numberOfItems = getReducedCartItems(
          orderDetail.pricing.comboItems
        ).length;

        return {
          id: orderDetail.preorder?.id || "",
          email: orderDetail.user.email,
          firstName: orderDetail.user.firstName,
          lastName: orderDetail.user.lastName,
          orderDate,
          poNumber: orderDetail.poNumber || "",
          clientProjectName,
          numberOfItems,
          orderDetail: orderDetail,
          projectNumber: techpackProjectNumber,
          shouldStartExpanded: !!(
            orderId && orderDetail.preorder?.id === orderId
          ),
        };
      }) || []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersData?.orders, formatShortDate, t]);

  return (
    <Box sx={styles.centerContainer}>
      {orderId && (
        <Message
          message={t("order_list.info.request_acknowledged.message")}
          messageIcon="check_circle"
          fullWidth={true}
          type={MessageTypes.SUCCESS}
          data-test="order.successMessage"
        />
      )}
      <Typography variant="h2">{t("order_list.title")}</Typography>
      <FlexColBox maxHeight="80vh" width="100%" height="100%">
        <Table
          columns={columns}
          data={rows}
          rowsPerPage={rowsPerPage}
          page={page}
          totalCount={ordersData.count}
          sortBy={sortBy}
          handleSort={setSortBy}
          handleChangePage={setCurrentPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleSearch={handleSearch}
          isRefetching={isRefetching}
          isInitialLoading={isInitialLoading}
        />
      </FlexColBox>
    </Box>
  );
};

export default memo(UserOrderList);
